import React from 'react';
import { useNotification } from '../../../../hooks/useNotification';
import { useErrorInputs } from '../../../../hooks/useErrorInputs';
import { useForm } from '../../../../hooks/useForm';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Toolbar from '@mui/material/Toolbar';
import AppBar from '@mui/material/AppBar';
import Dialog from '@mui/material/Dialog';
import Slide from '@mui/material/Slide';
import Box from '@mui/material/Box';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import { ManageConfiguration } from '../../components/manageconfiguration/ManageConfiguration';
import { ManageFlowProcess } from '../../components/manageFlowProcess/ManageFlowProcess';
import { MenuCompanyOption } from '../../components/manageconfiguration/MenuCompanyOption';
import { ActionIconItem } from '../../../../components/others/ActionIconItem';
import { ActionButtons } from '../../../../components/others/ActionButtons';
import { ManageActions } from '../manageconfiguration/ManageActions';
import { normalizeConfig, parseFlowProcess, sanitizeAction } from '../../helpers/helpers';
import {
    CHILD_TYPE_ACTIONS,
    EMPTY_ACTION,
    MENU_TYPE_ACTIONS,
    MOCKCONFIG,
    TABLE_PREVIEW_TYPES,
} from '../../constants/interface.constants';
import { PreviewTable } from './PreviewTable';
import { safeParseJSON } from '../../../../utils/jsonValidator';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const PreviewTableToolbar = ({
    json,
    typeAction,
    isEdit,
    isMenu,
    isSubmenu,
    isButton,
    headerButtons,
    onChange,
    onItemClick,
    onOpenFullscreen,
    t,
}) => {
    const configData = safeParseJSON(json.configuration);

    return (
        <Box sx={{ mb: 2, pt: 1 }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 2,
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        {(isMenu || isSubmenu) && (
                            <ManageConfiguration
                                data={configData}
                                typeAction={typeAction}
                                isDisabled={!typeAction}
                                isEdit={isEdit}
                                addedConfiguration={onChange}
                                t={t}
                                action="listColumns"
                                inline
                            />
                        )}

                        {(isMenu || isSubmenu) && (
                            <ManageFlowProcess
                                data={json.flow_process}
                                typeAction={typeAction}
                                isDisabled={!typeAction}
                                isEdit={isEdit}
                                addedFlowProcess={onChange}
                                t={t}
                                inline
                            />
                        )}

                        {isMenu && (
                            <ManageConfiguration
                                data={configData}
                                typeAction={typeAction}
                                isDisabled={!typeAction}
                                isEdit={isEdit}
                                addedConfiguration={onChange}
                                t={t}
                                action="createButton"
                                inline
                            />
                        )}

                        {isButton && (
                            <Tooltip title="Configurar botón de cabecera">
                                <IconButton
                                    size="small"
                                    onClick={() => onItemClick(safeParseJSON(json.configuration) || EMPTY_ACTION, 0, 'header')}
                                >
                                    <SettingsIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}

                        <Tooltip title={t('interface.fullscreenPreview')}>
                            <IconButton size="small" onClick={onOpenFullscreen}>
                                <OpenInFullIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {isMenu && (
                        <MenuCompanyOption configuration={json.configuration} onChange={onChange} t={t} inline />
                    )}
                </Box>

                {(isMenu || isButton) && (
                    <Box
                        className="box-actions-header"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'wrap',
                            justifyContent: 'flex-end',
                            flexShrink: 0,
                            alignSelf: 'stretch',
                        }}
                    >
                        {headerButtons.map((item, i) => (
                            <ActionIconItem
                                key={`header-btn-${i}`}
                                item={item}
                                onClick={() => onItemClick(item, i, 'header')}
                                height={35}
                            />
                        ))}
                    </Box>
                )}
            </Box>

            {(isMenu || isButton) && (
                <Box sx={{ mt: 1 }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '18px' }}>
                        {json?.name || 'Vista previa'}
                    </Typography>
                    {json?.description && (
                        <Typography variant="body2" color="text.secondary">
                            {json.description}
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
};

const PreviewContent = ({
    config,
    json,
    typeAction,
    isEdit,
    isMenu,
    isSubmenu,
    isButton,
    onItemClick,
    headerButtons = [],
    rowActions = [],
    onChange,
    onOpenFullscreen,
    t,
    openFullPreview,
    showToolbar = true,
}) => {
    const tableColumns = config?.table?.length ? config.table : MOCKCONFIG.table;

    return (
        <Box sx={{ width: '100%', mt: openFullPreview ? 2 : 0, px: 2 }} className="custom-hard-table-zd-nix">
            {showToolbar && (
                <PreviewTableToolbar
                    json={json}
                    typeAction={typeAction}
                    isEdit={isEdit}
                    isMenu={isMenu}
                    isSubmenu={isSubmenu}
                    isButton={isButton}
                    headerButtons={headerButtons}
                    onChange={onChange}
                    onItemClick={onItemClick}
                    onOpenFullscreen={onOpenFullscreen}
                    t={t}
                />
            )}

            <PreviewTable
                columns={tableColumns}
                fullJson={json}
                rowActions={rowActions}
                handleToggleFullscreenPreviewEditor={onItemClick}
            />
        </Box>
    );
};

export const PreviewRenderer = ({ json = {}, onChange, t = (k) => k, isEdit }) => {
    const [values, handleInputChange, handleDirectInputChange, setAllInfo] = useForm(EMPTY_ACTION);
    const [errorInputs, , resetErrors, validForm] = useErrorInputs({});
    const [sendNotification] = useNotification();
    const [openFullPreview, setOpenFullPreview] = React.useState(false);
    const [openActionEditor, setOpenActionEditor] = React.useState(false);
    const [selectedIndex, setSelectedIndex] = React.useState(null);
    const [editContext, setEditContext] = React.useState(null);

    const typeAction = json.type_action;
    const isMenu = typeAction === 'MENU';
    const isSubmenu = typeAction === 'SUBMENU';
    const isTableView = TABLE_PREVIEW_TYPES.includes(typeAction) || MENU_TYPE_ACTIONS.includes(typeAction);
    const isButton = typeAction === 'BUTTON';
    const isFlowProcess = typeAction === 'FLOW_PROCESS';

    const config = React.useMemo(() => {
        const parsed = normalizeConfig(json);
        if (!parsed?.table?.length && (isMenu || isSubmenu)) {
            return { ...MOCKCONFIG, button: parsed?.button || [] };
        }
        return parsed;
    }, [json, isMenu, isSubmenu]);

    const headerButtons = React.useMemo(() => {
        if (isButton) {
            const parsed = safeParseJSON(json.configuration);
            return parsed?.accion ? [parsed] : [];
        }
        return ensureArray(config.button);
    }, [config.button, isButton, json.configuration]);

    const rowActions = React.useMemo(() => {
        if (isFlowProcess) {
            const parsed = safeParseJSON(json.configuration);
            return parsed?.accion ? [parsed] : [];
        }
        return parseFlowProcess(json?.flow_process);
    }, [json?.flow_process, json?.configuration, isFlowProcess]);

    const handleItemClick = (action, index, context) => {
        resetErrors();
        setSelectedIndex(index);
        setEditContext(context);
        setAllInfo(action);
        setOpenActionEditor(true);
    };

    const handleCloseActionEditor = () => {
        setOpenActionEditor(false);
        resetErrors();
        setAllInfo(EMPTY_ACTION);
        setSelectedIndex(null);
        setEditContext(null);
    };

    const handleSaveAction = () => {
        if (!validForm(['accion', 'icon', 'color', 'type'], values)) {
            sendNotification({ msg: 'Debe completar el formulario.', variant: 'error' });
            return;
        }
        if (typeof onChange !== 'function') {
            handleCloseActionEditor();
            return;
        }

        const action = sanitizeAction(values);

        if (isFlowProcess || (isButton && editContext === 'header')) {
            onChange('configuration', action);
        } else if (editContext === 'header') {
            const currentConfig = safeParseJSON(json.configuration);
            const currentButtons = ensureArray(currentConfig?.button);
            const updatedButtons = selectedIndex === null
                ? [...currentButtons, action]
                : currentButtons.map((item, i) => (i === selectedIndex ? sanitizeAction({ ...item, ...values }) : item));
            onChange('configuration', { ...currentConfig, button: updatedButtons });
        } else {
            const currentFlow = parseFlowProcess(json?.flow_process);
            const updatedFlow = selectedIndex === null
                ? [...currentFlow, action]
                : currentFlow.map((item, i) => (i === selectedIndex ? sanitizeAction({ ...item, ...values }) : item));
            onChange('flow_process', updatedFlow);
        }
        handleCloseActionEditor();
    };

    const previewProps = {
        config,
        json,
        typeAction,
        isEdit,
        isMenu,
        isSubmenu,
        isButton,
        onItemClick: handleItemClick,
        headerButtons,
        rowActions,
        onChange,
        onOpenFullscreen: () => setOpenFullPreview(true),
        t,
        openFullPreview,
        showToolbar: isMenu || isSubmenu || isButton,
    };

    return (
        <>
            {isFlowProcess && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 2, mb: 1 }}>
                    <Tooltip title="Configurar acción de línea">
                        <IconButton
                            size="small"
                            onClick={() => handleItemClick(safeParseJSON(json.configuration) || EMPTY_ACTION, 0, 'row')}
                        >
                            <SettingsIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('interface.fullscreenPreview')}>
                        <IconButton size="small" onClick={() => setOpenFullPreview(true)}>
                            <OpenInFullIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            {isTableView && <PreviewContent {...previewProps} />}

            {isFlowProcess && (
                <Box sx={{ px: 2 }}>
                    <PreviewTable
                        columns={MOCKCONFIG.table}
                        fullJson={json}
                        rowActions={rowActions}
                        handleToggleFullscreenPreviewEditor={handleItemClick}
                    />
                </Box>
            )}

            <Dialog fullScreen open={openFullPreview} onClose={() => setOpenFullPreview(false)} TransitionComponent={Transition}>
                <AppBar sx={{ position: 'relative' }}>
                    <Toolbar>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
                            {json?.name || 'Preview'}
                        </Typography>
                        <IconButton edge="end" color="inherit" onClick={() => setOpenFullPreview(false)}>
                            <CloseFullscreenIcon />
                        </IconButton>
                    </Toolbar>
                </AppBar>
                <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                    {(isTableView || isFlowProcess) && (
                        <PreviewContent
                            {...previewProps}
                            openFullPreview
                            showToolbar={isMenu || isSubmenu || isButton}
                        />
                    )}
                </Box>
            </Dialog>

            <Dialog open={openActionEditor} onClose={handleCloseActionEditor} fullWidth maxWidth="md" TransitionComponent={Transition}>
                <DialogTitle>
                    <Typography variant="h5" component="span">
                        {editContext === 'header' ? 'Editar botón de cabecera' : 'Editar acción de línea'}
                    </Typography>
                    <IconButton onClick={handleCloseActionEditor} sx={{ position: 'absolute', right: 8, top: 8, color: 'grey.500' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <ManageActions
                        values={values}
                        errorInputs={errorInputs}
                        listActions={false}
                        handleInputChange={handleInputChange}
                        handleDirectInputChange={handleDirectInputChange}
                        t={t}
                    />
                </DialogContent>
                <DialogActions>
                    <ActionButtons onSubmit={handleSaveAction} onCancel={handleCloseActionEditor} />
                </DialogActions>
            </Dialog>
        </>
    );
};

function ensureArray(value) {
    return Array.isArray(value) ? value : [];
}
